/**
 * The shared upload dropzone surface — the react-dropzone root UI extracted from
 * UploadDialog so the library upload dialog and the org template upload dialog
 * render the exact same drop target. The caller owns `useDropzone` (its accept
 * map, size cap, and transport differ), passing the returned prop getters plus
 * the copy strings in.
 */
import { Upload } from "lucide-react";
import type { DropzoneInputProps, DropzoneRootProps } from "react-dropzone";

import { cn } from "@/lib/utils";

interface UploadDropzoneProps {
    getRootProps: (props?: DropzoneRootProps) => DropzoneRootProps;
    getInputProps: (props?: DropzoneInputProps) => DropzoneInputProps;
    isDragActive: boolean;
    /** Primary line shown when idle, e.g. "Drop PDFs, Excel or CSV here or click to browse". */
    idleLabel: string;
    /** Secondary hint line, e.g. "PDF, XLSX or CSV up to 200MB each". */
    hint: string;
}

export function UploadDropzone({
    getRootProps,
    getInputProps,
    isDragActive,
    idleLabel,
    hint,
}: UploadDropzoneProps) {
    return (
        <div
            {...getRootProps()}
            data-slot="upload-dropzone"
            className={cn(
                "flex min-h-[120px] cursor-pointer flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed p-6 text-center transition-colors",
                isDragActive
                    ? "border-primary bg-primary/[0.04]"
                    : "border-border hover:border-primary hover:bg-primary/[0.02]"
            )}
        >
            <input {...getInputProps()} />
            <Upload
                size={28}
                className={cn(
                    "mb-1",
                    isDragActive ? "text-primary" : "text-muted-foreground"
                )}
            />
            <p
                className={cn(
                    "text-sm font-medium",
                    isDragActive ? "text-primary" : "text-foreground"
                )}
            >
                {isDragActive ? "Drop files here" : idleLabel}
            </p>
            <p className="text-muted-foreground text-xs">{hint}</p>
        </div>
    );
}
